/**
 * Copyright (c) 2010-2023 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { inject, Injectable } from '@angular/core';
import { FeatureService } from '@map/services/feature.service';

import { MapService } from '@map/services/map.service';
import { QueryService } from '@map/services/query.service';
import { SetLayerDisplayFeatures } from '@map/state/active-map.actions';
import { DisplayFeatures } from '@map/state/active-map.model';
import { ActiveMapState } from '@map/state/active-map.state';
import { SelectionFeatures } from '@map/state/selection.model';
import { SelectionState } from '@map/state/selection.state';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { DashGisUtil } from '@shared/dash-gis-util';
import { LoggerService } from '@shared/services/logger.service';

import { GeometryCollection as JstsGeometryCollection, GeometryFactory, PrecisionModel } from 'jsts/org/locationtech/jts/geom';

import { OL3Parser } from 'jsts/org/locationtech/jts/io';
import { BufferOp } from 'jsts/org/locationtech/jts/operation/buffer';
import { UnaryUnionOp } from 'jsts/org/locationtech/jts/operation/union';

// this is because of incomplete migration to ES6
import 'jsts/org/locationtech/jts/monkey.js';
import { Feature } from 'ol';
import { GeoJSON } from 'ol/format';
import { GeometryCollection, LineString, MultiLineString, MultiPoint, MultiPolygon, Point, Polygon } from 'ol/geom';
import LinearRing from 'ol/geom/LinearRing';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, take, tap } from 'rxjs/operators';
import { CreateBuffer } from './buffer.actions';

@Injectable({ providedIn: 'root' })
export class BufferService {
    hasBuffer$ = new BehaviorSubject<boolean>(false);

    private features$: Observable<DisplayFeatures> = inject(Store).select(ActiveMapState.displayFeatures);
    private selection$: Observable<SelectionFeatures> = of({}); //inject(Store).select(SelectionState.selection);

    constructor(
        private logger: LoggerService,
        private queryService: QueryService,
        private featureService: FeatureService,
        private mapService: MapService,
        private actions$: Actions,
        private store: Store
    ) {
        this.actions$.pipe(ofActionSuccessful(CreateBuffer)).subscribe((action: CreateBuffer) => {
            this.createBuffer(action.bufferDistance, action.selectWithin);
        });

        this.features$
            .pipe(
                filter((f: DisplayFeatures) => f['buffer'] && Array.isArray(f['buffer'])),
                distinctUntilChanged(),
                tap((features) => {
                    const bufferFeatures = features['buffer'] || [];
                    this.hasBuffer$.next(bufferFeatures.length > 0);
                })
            )
            .subscribe((features: DisplayFeatures) => {
                const bufferLayer = this.getBufferLayer();
                bufferLayer.getSource().refresh();
                const bufferFeatures: Feature[] = this.featureService.getFeatures(features['buffer']);
                if (bufferFeatures.length > 0) {
                    bufferLayer.getSource().addFeatures(bufferFeatures);
                }
            });
    }

    public createBuffer(bufferDistance, selectWithin) {
        this.selection$.pipe(take(1)).subscribe((selection: SelectionFeatures) => {
            const selectionMap = new Map<string, Feature[]>();
            for (const layer in selection) {
                if (selection.hasOwnProperty(layer)) {
                    const layerFeatures = this.featureService.getFeatures(selection[layer]);
                    selectionMap.set(layer, layerFeatures);
                }
            }

            if (selectionMap.size === 0) {
                return false;
            }
            // get buffer layer

            const source = this.getBufferLayer().getSource();
            const existingBuffers = source.getFeatures();

            const bufferedFeature = this.createBufferFeature(selectionMap, bufferDistance, existingBuffers);

            this.store
                .dispatch(
                    new SetLayerDisplayFeatures({
                        displayLayer: 'buffer',
                        featureIds: [ bufferedFeature.getId().toString() ]
                    })
                )
                .subscribe(() => {
                    if (selectWithin) {
                        this.selectWithinBuffer();
                    }
                });
        });
    }

    public selectWithinBuffer() {
        const bufferLayer = this.getBufferLayer();
        const source = bufferLayer.getSource();
        const bufferedFeatures = source.getFeatures();
        this.queryService.selectWithinFeature$(bufferedFeatures, 'Intersects').subscribe();
    }

    public clearBuffer() {
        const bufferLayer = this.getBufferLayer();
        bufferLayer.getSource().clear();
    }

    private getBufferLayer(): VectorLayer<VectorSource> {
        const source = new VectorSource({
            format: new GeoJSON({
                dataProjection: this.mapService.projection,
                featureProjection: this.mapService.projection
            })
        });
        return this.mapService.getVectorLayer('buffer', source);
    }

    private createBufferFeature(
        selectionFeatures: Map<string, Feature[]>,
        bufferDistance: number,
        existingBuffers: Feature[]
    ): Feature {
        const pm = new PrecisionModel();
        const geomFactory = new GeometryFactory(pm);
        const parser = new OL3Parser(geomFactory, undefined);
        parser.inject(
            Point,
            LineString,
            LinearRing,
            Polygon,
            MultiPoint,
            MultiLineString,
            MultiPolygon,
            GeometryCollection
        );
        const geomArray = [];
        selectionFeatures.forEach((featureCollection) => {
            featureCollection.forEach((feature) => {
                const geom = feature.getGeometry().transform(this.mapService.projection.getCode(), 'EPSG:2163');
                const g = parser.read(geom);
                geomArray.push(BufferOp.bufferOp(g, bufferDistance));
            });
        });
        existingBuffers.forEach((existingBuffer) => {
            const geom = existingBuffer.getGeometry().transform(this.mapService.projection.getCode(), 'EPSG:2163');
            const g = parser.read(geom);
            geomArray.push(g);
        });
        const geomCollection = new JstsGeometryCollection(geomArray, geomFactory);

        const unifiedGeom = UnaryUnionOp.union(geomCollection); // geomCollection.union();
        const f = new Feature(parser.write(unifiedGeom));
        f.getGeometry().transform('EPSG:2163', this.mapService.projection.getCode());
        f.setId(`buffer.${ DashGisUtil.uid() }`);
        this.featureService.addFeature(f);
        return f;
    }
}
